import apiClient from './client';

export interface ProductMovement {
  id: string;
  product: string | any;
  personnel?: string | any;
  type: 'Assignment' | 'Return' | 'Transfer' | 'Repair' | 'BranchTransfer';
  serialNumber: string;
  date: string;
  fromLocation?: string;
  fromLocationId?: string;
  toLocation: string;
  toLocationId?: string;
  notes?: string;
  branch: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateMovementRequest {
  product: string;
  personnel?: string;
  type: string;
  serialNumber: string;
  date: string;
  fromLocation?: string;
  fromLocationId?: string;
  toLocation: string;
  toLocationId?: string;
  notes?: string;
}

export interface UpdateMovementRequest {
  product?: string;
  personnel?: string;
  type?: string;
  serialNumber?: string;
  date?: string;
  fromLocation?: string;
  fromLocationId?: string;
  toLocation?: string;
  toLocationId?: string;
  notes?: string;
}

export interface StockReport {
  product: string;
  productName: string;
  totalStock: number;
  totalSales: number;
  totalPurchases: number;
  totalReturns: number;
}

export interface MovementsResponse {
  data: ProductMovement[];
  total: number;
  page: number;
  totalPages: number;
}

export interface MovementFilters {
  serialNumber?: string;
  branchId?: string;
  productId?: string;
  type?: string;
  personnelId?: string;
  personnelSearch?: string;
  departmentId?: string;
  page?: number;
  limit?: number;
}

export const inventoryApi = {
  getAllMovements: async (filters?: MovementFilters): Promise<MovementsResponse> => {
    const params = new URLSearchParams();
    if (filters?.serialNumber) params.append('serialNumber', filters.serialNumber);
    if (filters?.branchId) params.append('branchId', filters.branchId);
    if (filters?.productId) params.append('productId', filters.productId);
    if (filters?.type) params.append('type', filters.type);
    if (filters?.personnelId) params.append('personnelId', filters.personnelId);
    if (filters?.personnelSearch) params.append('personnelSearch', filters.personnelSearch);
    if (filters?.departmentId) params.append('departmentId', filters.departmentId);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const response = await apiClient.get(`/inventory/movements?${params.toString()}`);
    return {
      data: response.data.data.map((item: any) => ({ ...item, id: item._id })),
      total: response.data.total,
      page: response.data.page,
      totalPages: response.data.totalPages,
    };
  },

  getMovementById: async (id: string): Promise<ProductMovement> => {
    const response = await apiClient.get(`/inventory/movements/${id}`);
    return { ...response.data, id: response.data._id };
  },

  createMovement: async (data: CreateMovementRequest): Promise<ProductMovement> => {
    const response = await apiClient.post('/inventory/movements', data);
    return { ...response.data, id: response.data._id };
  },

  updateMovement: async (id: string, data: UpdateMovementRequest): Promise<ProductMovement> => {
    const response = await apiClient.put(`/inventory/movements/${id}`, data);
    return { ...response.data, id: response.data._id };
  },

  deleteMovement: async (id: string): Promise<void> => {
    await apiClient.delete(`/inventory/movements/${id}`);
  },

  getStockReport: async (): Promise<StockReport[]> => {
    const response = await apiClient.get('/inventory/stock-report');
    return response.data;
  },

  getMovementsByPersonnel: async (personnelId: string): Promise<ProductMovement[]> => {
    const response = await apiClient.get(`/inventory/movements/by-personnel/${personnelId}`);
    return response.data.map((item: any) => ({ ...item, id: item._id }));
  },

  getMovementsByUser: async (userId: string): Promise<ProductMovement[]> => {
    const response = await apiClient.get(`/inventory/movements/by-user/${userId}`);
    return response.data.map((item: any) => ({ ...item, id: item._id }));
  },
};
